<script setup lang="ts">
import {
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogRoot,
  AlertDialogTitle
} from "reka-ui"
import { reactive, watch } from "vue"
import { conflictPreview } from "../conflictPreview"
import type { ConflictChoice, FieldConflict } from "../types"

const props = defineProps<{
  open: boolean
  conflicts: FieldConflict[]
}>()

const emit = defineEmits<{
  "resolve": [choices: Record<string, ConflictChoice>]
  "cancel": []
}>()

const choices = reactive<Record<string, ConflictChoice>>({})

// Bei jedem Oeffnen (bzw. neuen Konflikten) auf "meine Fassung" zuruecksetzen.
watch(
  () => [props.open, props.conflicts] as const,
  () => {
    for (const key of Object.keys(choices)) delete choices[key]
    for (const c of props.conflicts) choices[c.key] = "mine"
  },
  { immediate: true }
)

function handleSave() {
  emit("resolve", { ...choices })
}

function handleCancel() {
  emit("cancel")
}

/** Kurzfassung plus vollstaendiger Text, falls die Kurzfassung gekuerzt ist. */
function preview(value: unknown): { short: string; full: string | null } {
  const short = conflictPreview(value)
  const full = conflictPreview(value, Infinity)
  return { short, full: full.length > short.length ? full : null }
}
</script>

<template>
  <AlertDialogRoot :open="open" @update:open="(v: boolean) => { if (!v) emit('cancel') }">
    <AlertDialogPortal>
      <AlertDialogOverlay class="bg-black/75 data-[state=open]:animate-overlayShow fixed inset-0 z-30" />
      <AlertDialogContent
        class="z-[100] text-[15px] data-[state=open]:animate-contentShow fixed top-[50%] left-[50%] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] overflow-y-auto rounded-[6px] bg-white p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none"
      >
        <AlertDialogTitle class="m-0 text-[17px] font-semibold">
          Inzwischen von jemand anderem geändert
        </AlertDialogTitle>
        <AlertDialogDescription class="mt-4 mb-2 text-[15px] leading-normal">
          Diese Felder wurden gleichzeitig geändert. Wähle je Feld, welche Fassung gespeichert wird.
        </AlertDialogDescription>

        <fieldset v-for="c in conflicts" :key="c.key" class="mt-4 border-t border-base-300 pt-4">
          <legend class="font-semibold">{{ c.label }}</legend>

          <label class="flex items-center gap-2">
            <input
              v-model="choices[c.key]"
              type="radio"
              :name="`conflict-${c.key}`"
              value="mine"
              :data-testid="`conflict-mine-${c.key}`"
              class="radio radio-sm"
            >
            Meine Fassung
          </label>
          <details v-if="preview(c.mine).full !== null" class="text-sm">
            <summary class="whitespace-pre-wrap">{{ preview(c.mine).short }}</summary>
            <p class="whitespace-pre-wrap text-sm">{{ preview(c.mine).full }}</p>
          </details>
          <p v-else class="whitespace-pre-wrap text-sm">{{ preview(c.mine).short }}</p>

          <label class="flex items-center gap-2 mt-2">
            <input
              v-model="choices[c.key]"
              type="radio"
              :name="`conflict-${c.key}`"
              value="theirs"
              :data-testid="`conflict-theirs-${c.key}`"
              class="radio radio-sm"
            >
            Gespeicherte Fassung
          </label>
          <details v-if="preview(c.theirs).full !== null" class="text-sm">
            <summary class="whitespace-pre-wrap">{{ preview(c.theirs).short }}</summary>
            <p class="whitespace-pre-wrap text-sm">{{ preview(c.theirs).full }}</p>
          </details>
          <p v-else class="whitespace-pre-wrap text-sm">{{ preview(c.theirs).short }}</p>
        </fieldset>

        <div class="flex justify-end gap-[25px] mt-6">
          <button
            type="button"
            data-testid="conflict-cancel"
            class="btn btn-ghost"
            @click="handleCancel"
          >
            Abbrechen
          </button>
          <button
            type="button"
            data-testid="conflict-save"
            class="btn btn-primary"
            @click="handleSave"
          >
            Speichern
          </button>
        </div>
      </AlertDialogContent>
    </AlertDialogPortal>
  </AlertDialogRoot>
</template>
